import { IRowData } from "@multiweb/types/contracts";
import { DateUtils } from "../../../Global/Base/Utils/DateUtils";
import { CultureInfo } from "../../../Global/Dom/CultureInfo";
import { registerCustomElement } from "../../UI.ElementarControls/Utils/CustomElementUtils";
import { CalendarEvent, CalendarEventType } from "./CalendarEvent";
import { CalendarEventItem, CalendarEventItemType } from "./CalendarEventItem";
import { DayCalendarGrid } from "./DayCalendarGrid";

export class CalendarHourEvent extends CalendarEvent {
    private _isSelected = false;

    //private _isSelected: boolean;
    public override get isSelected(): boolean {
        if (this.checkAndLogDisposedUsage("get isSelected")) return false;

        return this._isSelected;
    }

    public override set isSelected(value: boolean) {
        if (this.checkAndLogDisposedUsage("set isSelected")) return;

        if (this._isSelected == value)
            return;
        this._isSelected = value;

        const items = this.getCalendarEventItems();

        const index = this._dataGrid.selectedRows.indexOf(this);
        if (value) {
            if (index == -1)
                this._dataGrid.selectedRows.push(this);
        } else {
            if (index != -1)
                this._dataGrid.selectedRows.splice(index, 1);
        }

        for (const item of items) {
            item.isSelected = value;
        }
    }

    public static override createEventByIRowData(ownerGrid: DayCalendarGrid, uniqeDSId: string, data: IRowData): CalendarHourEvent {
        const ce = this.createEvent(ownerGrid, uniqeDSId, data, CalendarEventType.range);

        return ce;
    }

    public static override createEvent(ownerGrid: DayCalendarGrid, uniqeDSId: string, data: IRowData, type: CalendarEventType): CalendarHourEvent {
        const ce = CalendarEvent._prepareEvent(new CalendarHourEvent(), ownerGrid, uniqeDSId, data);

        const curCult = CultureInfo.getCultureInfo();
        const curCultName = curCult.name;
        const timeOptions: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit" };

        let sDateHour = new Date(ce.dateFrom);
        let eDateHour = new Date(ce.dateTo);
        const currentDate = new Date(ownerGrid.currentDate);
        currentDate.setHours(0, 0, 0, 0);
        const lastDay = ownerGrid.dateSpan;

        const nextDay = new Date(currentDate);
        nextDay.setDate(nextDay.getDate() + 1);

        if (sDateHour < currentDate) sDateHour = new Date(currentDate);
        if (eDateHour > lastDay) eDateHour = new Date(lastDay);

        let lastCEI: CalendarEventItem = null;

        const dateForCompare = new Date(sDateHour);
        dateForCompare.setDate(dateForCompare.getDate() + 1);
        if (CalendarHourEvent._isDayBeginning(sDateHour) && CalendarHourEvent._isDayBeginning(eDateHour) && DateUtils.compareDateTimes(dateForCompare, eDateHour)) {
            lastCEI = new CalendarEventItem();
            lastCEI.type = CalendarEventItemType.allday;
            ce._initOneItem(lastCEI);
            lastCEI.currentDate = new Date(sDateHour);
            lastCEI.caption = ce.caption;
            ce._calItems.set(`${sDateHour.getMinutes()}|${sDateHour.getHours()}|${sDateHour.getDate()}|${sDateHour.getMonth()}|${sDateHour.getFullYear()}`, lastCEI); // `

            lastCEI.mouseOver.add((_sender, prm) => {
                ce._calItems.forEach((aktItem) => aktItem.mouseOverStyle = prm);
            });

            sDateHour.setDate(sDateHour.getDate() + 1);
            currentDate.setDate(currentDate.getDate() + 1);
            nextDay.setDate(nextDay.getDate() + 1);

            return ce;
        }

        if (sDateHour <= currentDate)
            sDateHour = currentDate;
        if (eDateHour >= lastDay)
            eDateHour = lastDay;

        let firstIteration = true;
        while (sDateHour < eDateHour || (DateUtils.compareDateTimes(sDateHour, eDateHour) && firstIteration)) {
            const id = `${sDateHour.getMinutes()}|${sDateHour.getHours()}|${sDateHour.getDate()}|${sDateHour.getMonth()}|${sDateHour.getFullYear()}`;
            lastCEI = CalendarHourEvent._createEventItem(ce, sDateHour, lastCEI, type, curCultName, timeOptions, id); // `

            if (DateUtils.compareDateTimes(sDateHour, eDateHour) && firstIteration) {
                firstIteration = false;
                if (sDateHour.getMinutes() != 0)
                    lastCEI.topOffset = sDateHour.getMinutes() / 60;
                else
                    lastCEI.topOffset = 0;
                lastCEI.bottomOffset = lastCEI.topOffset + 15;
                continue;
            }

            if (sDateHour.getMinutes() != 0) {
                lastCEI.topOffset = sDateHour.getMinutes() / 60;
            }
            const sDateWithoutMinutes = new Date(sDateHour);
            sDateWithoutMinutes.setMinutes(0, 0, 0);

            const eDateWithoutMinutes = new Date(eDateHour);
            eDateWithoutMinutes.setMinutes(0, 0, 0);

            if (DateUtils.compareDateTimes(sDateWithoutMinutes, eDateWithoutMinutes) && eDateHour.getMinutes() != 0) {
                lastCEI.bottomOffset = (1 - (eDateHour.getMinutes() / 60));
            }

            sDateHour.setHours(sDateHour.getHours() + 1);
            sDateHour.setMinutes(0);

            firstIteration = false;
        }

        if (lastCEI)
            if (ce._calItems.size == 1)
                lastCEI.type = CalendarEventItemType.standalone;
            else
                lastCEI.type = CalendarEventItemType.end;

        return ce;
    }
    protected static override _createEventItem(ce: CalendarHourEvent, sDate: Date, lastCEI: CalendarEventItem, type: CalendarEventType, curCultName: string, timeOptions: Intl.DateTimeFormatOptions, id: string): CalendarEventItem {
        const cei = new CalendarEventItem();

        cei.type = lastCEI == null ? CalendarEventItemType.start : CalendarEventItemType.continuously;

        ce._initOneItem(cei);

        cei.currentDate = new Date(sDate);

        if (cei.type == CalendarEventItemType.start || sDate.getHours() == 0) {
            cei.caption = ce.caption;
            if (type != CalendarEventType.allDay)
                cei.timeText = ce.dateFrom.toLocaleTimeString(curCultName, timeOptions);
        }
        ce._calItems.set(id, cei);

        cei.mouseOver.add((_sender, prm) => {
            ce._calItems.forEach((aktItem) => aktItem.mouseOverStyle = prm);
        });

        return cei;
    }

    private static _isDayBeginning(d: Date): boolean {
        return d.getHours() == 0 && d.getMinutes() == 0 && d.getSeconds() == 0;
    }

    // @ts-ignore // nevyužito, ale ponecháno
    private static _isAllDayEvent(d1: Date, d2: Date, currentDate: Date, firstHour: number, lastHour: number): boolean {
        return (d1 < currentDate || (d1 == currentDate && d1.getHours() < firstHour)) && (d2 > currentDate || (d2 == currentDate && d2.getHours() > lastHour));
    }

    // @ts-ignore // nevyužito, ale ponecháno
    private static _isCurrentDay(d1: Date, currentDate: Date, firstHour: number, lastHour: number): boolean {
        return d1.getDate() == currentDate.getDate() && d1.getMonth() == currentDate.getMonth() && d1.getFullYear() == currentDate.getFullYear() && d1.getHours() >= firstHour && d1.getHours() < lastHour;
    }
}

registerCustomElement("tsc-calendargridCalendarHourEvent", CalendarHourEvent);
